import styles from '@components/components/Layout/Layout.module.scss';
import Navigation from '@components/components/Navigation/Navigation';
import ReducedNavigation from '@components/components/ReducedNavigation/ReducedNavigation';
import Footer from '@components/components/Footer/Footer';
import {
  useOnlimZIndexObserver,
  virtualPageViewTracking,
} from '@components/hooks';
import { usePageData } from '@components/context';
import InfoBar from '@components/components/InfoBar/InfoBar';
import LogoOnlyNavigation from '@components/components/LogoOnlyNavigation/LogoOnlyNavigation';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { VisuallyHidden } from '@ariakit/react';

// Opens BotTina with respective hash.
const handleBottinaHash = () => {
  const hash = window.location.hash;

  if (hash === '#chat-bottina' && window.OnlimCw) {
    window.OnlimCw('open');
    window.history.replaceState(null, null, ' ');
  }
};

const scrollToAnchorAfterMounting = () => {
  if (!window.location.hash) {
    return;
  }

  try {
    // Ensures that also selectors starting with numbers
    // and special characters are interpreted correctly.
    const decodedHash = decodeURIComponent(window.location.hash.substring(1));
    const selector = `[id="${decodedHash}"]`;
    const element = document.querySelector(selector);

    if (element) {
      setTimeout(() => {
        element.scrollIntoView({
          behavior: 'smooth',
        });
      }, 300);
    }
  } catch (err) {
    /* empty */
  }
};

const onHashChange = () => {
  if (window.location.hash === '#chat-bottina') {
    handleBottinaHash();
    return;
  }
  scrollToAnchorAfterMounting();
};

const NavigationDecider = ({ hideNavigation, theme }) => {
  const { layoutStructure } = usePageData();

  switch (layoutStructure.navigationType) {
    case 'reduced':
      return <ReducedNavigation />;
    case 'logo':
      return <LogoOnlyNavigation theme={theme} />;
    default:
      // hide regular navigation if disabled
      return !hideNavigation ? <Navigation /> : null;
  }
};

const Layout = ({
  skipContentRef,
  organicHeaderBg = 'orange',
  className,
  children,
  theme,
}) => {
  const [isInfobarShown, setIsInfobarShown] = useState(false);
  const {
    categories,
    layoutStructure,
    parents,
    postTags,
    rootLevel,
    title: currentTitle,
    type,
  } = usePageData();
  useOnlimZIndexObserver();

  useEffect(() => {
    virtualPageViewTracking(
      postTags,
      categories,
      rootLevel,
      currentTitle,
      parents,
    );
  }, []);

  useEffect(() => {
    scrollToAnchorAfterMounting();
    handleBottinaHash();

    window.addEventListener('hashchange', onHashChange);

    return () => {
      window.removeEventListener('hashchange', onHashChange);
    };
  }, []);

  return (
    <>
      <header>
        <VisuallyHidden>
          <a
            className="sr-only skip-link sr-only-focusable"
            href="#content"
            ref={skipContentRef}
          >
            Zum Inhalt springen
          </a>
        </VisuallyHidden>
        <InfoBar setShow={setIsInfobarShown} show={isInfobarShown} />
      </header>
      <div
        className={classNames(
          styles.container,
          {
            [styles.orange]: organicHeaderBg === 'orange',
            [styles.orangeBar]: organicHeaderBg === 'orange-bar',
            [styles.business]: type === 'category-products-business',
            [styles.orangeRed]: organicHeaderBg === 'orange-red',
            [styles.orangeBlue]: organicHeaderBg === 'orange-blue',
            [styles.longOrangeBlue]: organicHeaderBg === 'long-orange-blue',
            [styles.longBlue]: organicHeaderBg === 'long-blue',
            [styles.blueGradient]: organicHeaderBg === 'blue-gradient',
            [styles.blueFull]: organicHeaderBg === 'blue-full',
            [styles.orangeRedBlueMix]: organicHeaderBg === 'orange-red-blue',
            [styles.logoOnly]: layoutStructure.navigationType === 'logo',
            [styles.infoBarLogoOnly]: isInfobarShown,
          },
          className,
        )}
      >
        <NavigationDecider
          hideNavigation={layoutStructure.hideNavigation}
          theme={theme}
        />
        <div className={styles.contentFooterWrapper}>
          <main
            className={classNames(styles.content, {
              [styles.withoutNavigation]: layoutStructure.hideNavigation,
              [styles.logoOnlyNavigation]:
                layoutStructure.navigationType === 'logo',
            })}
            id="content"
          >
            {children}
          </main>
          {!layoutStructure.hideFooter && <Footer />}
        </div>
      </div>
    </>
  );
};

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  organicHeaderBg: PropTypes.string,
};

export default Layout;
